import type { JSONContent } from '@tiptap/core';
import { unescapeBackslashes, unescapeTildes } from '../utils/markdown.util';
import { BLOCK_MATH_PATTERN, INLINE_MATH_PATTERN } from '../utils/math.util';

export interface MathNodeAttrs {
  latex: string;
}

const BLOCK_MATH_REGEX = new RegExp(`^${BLOCK_MATH_PATTERN}$`);
const INLINE_MATH_REGEX = new RegExp(INLINE_MATH_PATTERN, 'g');

/**
 * Recursively processes a node to convert math syntax in text to math nodes.
 */
export function processNodeForMath(node: JSONContent): JSONContent {
  // Check for block math (standalone paragraph with only $$...$$)
  if (node.type === 'paragraph' && node.content?.length === 1) {
    const textNode = node.content[0];
    if (textNode.type === 'text' && textNode.text) {
      const trimmed = textNode.text.trim();
      const blockMatch = BLOCK_MATH_REGEX.exec(trimmed);
      if (blockMatch) {
        return {
          type: 'blockMath',
          attrs: { latex: unescapeBackslashes(unescapeTildes(blockMatch[1].trim())) },
          ...(textNode.marks && { marks: textNode.marks }),
        };
      }
    }
  }

  // Process inline content for inline math
  if (node.content) {
    node = {
      ...node,
      content: processContentForInlineMath(node.content),
    };
  }

  return node;
}

/**
 * Processes an array of content nodes, splitting text nodes that contain inline math.
 */
function processContentForInlineMath(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'text' && node.text) {
      const parts = splitTextWithInlineMath(node.text, node.marks);
      result.push(...parts);
    } else if (node.content) {
      // Recursively process nested content
      result.push({
        ...node,
        content: processContentForInlineMath(node.content),
      });
    } else {
      result.push(node);
    }
  }

  return result;
}

/**
 * Splits a text string into text nodes and inlineMath nodes.
 */
function splitTextWithInlineMath(text: string, marks?: JSONContent['marks']): JSONContent[] {
  const result: JSONContent[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(INLINE_MATH_REGEX)) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      result.push({ type: 'text', text: beforeText, ...(marks && { marks }) });
    }

    // Add the inline math node, preserving marks (e.g., popover marks)
    result.push({
      type: 'inlineMath',
      attrs: { latex: unescapeBackslashes(unescapeTildes(match[1])) },
      ...(marks && { marks }),
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    result.push({ type: 'text', text: remainingText, ...(marks && { marks }) });
  }

  // If no math was found, return original text node
  if (result.length === 0) {
    result.push({ type: 'text', text, ...(marks && { marks }) });
  }

  return result;
}

/**
 * Recursively converts math nodes back to text with LaTeX syntax.
 */
export function convertMathNodesToText(node: JSONContent): JSONContent {
  // Convert blockMath to paragraph with $$...$$
  if (node.type === 'blockMath') {
    const latex = (node.attrs as MathNodeAttrs | undefined)?.latex ?? '';
    return {
      type: 'paragraph',
      content: [{ type: 'text', text: `$$${latex}$$`, ...(node.marks && { marks: node.marks }) }],
    };
  }

  // Process content array to convert inlineMath nodes
  if (node.content) {
    return {
      ...node,
      content: convertMathNodesInContent(node.content),
    };
  }

  return node;
}

/**
 * Processes content array, converting inlineMath nodes to text with $...$ syntax.
 */
function convertMathNodesInContent(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'inlineMath') {
      const latex = (node.attrs as MathNodeAttrs | undefined)?.latex ?? '';
      result.push({ type: 'text', text: `$${latex}$`, ...(node.marks && { marks: node.marks }) });
    } else if (node.content) {
      // Recursively process nested content
      result.push(convertMathNodesToText(node));
    } else {
      result.push(node);
    }
  }

  return result;
}
